import { LoggerService } from '@angular-ru/cdk/logger';
import { inject, Pipe, PipeTransform } from '@angular/core';
import { IconName, IconPrefix, IconProp } from '@fortawesome/fontawesome-svg-core';
import { isSupportedIconName } from '../services/icon.service';

/**
 * Rendered when the requested icon can't be resolved to a registered one.
 */
const DEFAULT_ICON: IconProp = ['fas', 'circle-question'];

/**
 * Assumed when no prefix is provided, and tried as a fallback style when the
 * requested prefix isn't registered.
 */
const DEFAULT_PREFIX: IconPrefix = 'fas';

/**
 * Converts a Drimify icon name into a FontAwesome IconProp, validating it
 * against the icons the IconService actually registers.
 *
 * Expected format: 'prefix:iconName' (e.g., 'far:bars')
 *
 * Behavior:
 * - Registered input: Returns [prefix, iconName] tuple
 * - No prefix: Assumes 'fas' (FontAwesome Solid)
 * - Unregistered input: Retries as 'fas:iconName' with a warning; if that
 *   isn't registered either, returns the ['fas', 'circle-question'] fallback
 *   so a visible placeholder renders instead of nothing
 * - Null/undefined/empty: Returns the fallback icon (no warning)
 * - Extra parts: Ignores parts beyond prefix:iconName
 *
 * @example
 * Valid inputs:
 * 'far:bars' → ['far', 'bars']
 * 'house' → ['fas', 'house'] (no prefix, defaults to 'fas')
 *
 * @example
 * Edge cases:
 * 'fal:gear' → ['fas', 'gear'] ('fal' isn't registered, but 'fas:gear' is — warns)
 * 'fas:not-real' → ['fas', 'circle-question'] (unknown icon — warns)
 * 'far:bars:extra' → ['far', 'bars'] (extra parts ignored)
 * null → ['fas', 'circle-question'] (fallback icon)
 */
@Pipe({
  name: 'faIconName',
})
export class FaIconNamePipe implements PipeTransform {
  private readonly loggerService = inject(LoggerService);

  transform(value: string | undefined | null): IconProp {
    if (!value) {
      return DEFAULT_ICON;
    }

    // No prefix provided — assume 'fas' (FontAwesome Solid).
    const qualified = value.includes(':') ? value : `${DEFAULT_PREFIX}:${value}`;

    // Keep only prefix and icon name; ignore extra parts if present.
    const [prefix, iconName] = qualified.split(':') as [IconPrefix, IconName];

    if (isSupportedIconName(`${prefix}:${iconName}`)) {
      return [prefix, iconName];
    }

    // The requested style isn't registered, but the solid variant might be
    // (e.g. 'fal:gear' → 'fas:gear').
    if (prefix !== DEFAULT_PREFIX && isSupportedIconName(`${DEFAULT_PREFIX}:${iconName}`)) {
      this.loggerService.warn(`[FaIconNamePipe] '${value}' is not registered — rendering '${DEFAULT_PREFIX}:${iconName}' instead.`);
      return [DEFAULT_PREFIX, iconName];
    }

    this.loggerService.warn(`[FaIconNamePipe] '${value}' is not a registered icon — rendering the fallback placeholder.`);
    return DEFAULT_ICON;
  }
}
